import { useRef, useState } from 'react'
import data from './emoji-data.json'
import { Avatar } from './avatar'
import { Button } from './button'
import { Input } from './input'
import { SearchIcon, SmilePlusIcon } from './icon'
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from './popover'

// Dataset and category order ported from Unni's article emoji picker.
type Emoji = { id: string; name: string; keywords: string[]; skins: { native: string }[] }
const emojis: Record<string, Emoji> = data.emojis
const searchTerms = Object.fromEntries(Object.values(emojis).map(emoji => [
  emoji.id,
  [emoji.id, emoji.name, ...emoji.keywords,
    ...Object.entries(data.aliases).filter(([, id]) => id === emoji.id).map(([alias]) => alias),
  ].join(' ').toLowerCase().replaceAll('_', ' '),
]))

export function EmojiPicker({
  value,
  imageSrc,
  onValueChange,
  className,
}: {
  value?: string
  imageSrc?: string
  onValueChange: (value: string) => void
  className?: string
}) {
  const hasIcon = Boolean(value) || Boolean(imageSrc)
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  function select(emoji: string) {
    onValueChange(emoji)
    setOpen(false)
    setFilter('')
  }

  return (
    <Popover open={open} onOpenChange={next => { setOpen(next); if (!next) setFilter('') }}>
      <PopoverTrigger
        data-testid="page-icon-picker"
        aria-label={hasIcon ? 'Change page icon' : 'Add page icon'}
        className={className}
        render={hasIcon ? <button aria-label="Change page icon" type="button" className="inline-flex cursor-pointer items-center justify-center rounded-2xl focus-visible:outline-2 focus-visible:outline-primary" /> : <Button variant="outlined" size="sm" />}
      >
        {value?.length ? value : imageSrc ? (
          <Avatar alt="" className="rounded-2xl bg-transparent" data-testid="content-blocks-editor-page-icon" name="" size="2xl" src={imageSrc} />
        ) : <><SmilePlusIcon />icon</>}
      </PopoverTrigger>
      <PopoverPopup initialFocus={inputRef} aria-label="Emoji picker" className="w-[410px] max-w-[calc(100vw-2rem)]" showArrow={false} align="start">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <PopoverTitle>Emojis</PopoverTitle>
            {hasIcon && <Button size="sm" variant="text" onClick={() => { select('') }}>Remove icon</Button>}
          </div>
          <Input ref={inputRef} data-testid="emoji-filter" aria-label="Filter emojis" placeholder="Filter…" value={filter} onInput={event => { setFilter(event.currentTarget.value) }} type="search" leading={<SearchIcon />} />
          <EmojiGrid filter={filter} select={select} />
        </div>
      </PopoverPopup>
    </Popover>
  )
}

// Keep dataset filtering and element creation inside the portal. Page renders
// must not build an emoji grid while the portal subtree is unmounted.
function EmojiGrid({ filter, select }: { filter: string; select: (emoji: string) => void }) {
  const terms = filter.trim().toLowerCase().replaceAll('_', ' ').split(/\s+/).filter(Boolean)
  const categories = data.categories.map(category => ({
    id: category.id,
    emojis: category.emojis.map(id => emojis[id]).filter(emoji =>
      terms.every(term => searchTerms[emoji.id].includes(term) || emoji.skins[0].native.includes(term)),
    ),
  })).filter(category => category.emojis.length > 0)

  return (
    <div className="h-72 overflow-y-auto" key={filter}>
      {categories.length === 0 ? <p className="text-on-surface-secondary py-8 text-center" role="status">No emojis found</p> : (
        <div className="flex flex-col gap-5">
          {categories.map(category => (
            <section key={category.id} aria-label={category.id} className="flex flex-col gap-3">
              <h3 className="text-on-surface-secondary text-center text-sm">{category.id}</h3>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(40px,1fr))] gap-1">
                {category.emojis.map(emoji => (
                  <button key={emoji.id} type="button" data-testid={`emoji-${emoji.id}`} aria-label={emoji.name} title={emoji.name}
                    className="hover:bg-surface-4 focus-visible:ring-primary flex size-10 cursor-pointer items-center justify-center rounded-lg text-3xl focus-visible:ring-2 focus-visible:outline-none"
                    onClick={() => { select(emoji.skins[0].native) }}>
                    {emoji.skins[0].native}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
